import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Users, Search, Mail, Key, RefreshCw, Trash2, UserPlus, Copy, Check, Shield, Building, Briefcase, Eye, Loader2, AlertTriangle, Send, UserX, UserCheck, Filter, Eraser } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import InvitationDialog from './InvitationDialog';
import { getToken } from '../services/tokenService';
import InstitutionalHero from './ui/InstitutionalHero';
import IdentifiantsActionDialog from './IdentifiantsActionDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Rôles avec couleurs (foncées pour meilleur contraste)
const ROLES_CONFIG = {
  super_admin: { label: 'Super Admin', color: 'bg-red-600 text-white', icon: Shield },
  admin: { label: 'Admin', color: 'bg-blue-600 text-white', icon: Shield },
  cooper: { label: "COOPER'S", color: 'bg-purple-600 text-white', icon: Building },
  expert: { label: 'EXPERT', color: 'bg-orange-600 text-white', icon: Briefcase },
  member: { label: 'Membre', color: 'bg-green-600 text-white', icon: Users },
  membre: { label: 'Membre', color: 'bg-green-600 text-white', icon: Users },
  beneficiaire_pro: { label: 'Bénéficiaire Pro', color: 'bg-teal-600 text-white', icon: Users },
  beneficiaire_particulier: { label: 'Particulier', color: 'bg-gray-600 text-white', icon: Users }
};

const STATUS_CONFIG = {
  actif: { label: 'Actif', color: 'bg-green-600 text-white' },
  inactif: { label: 'Inactif', color: 'bg-gray-500 text-white' },
  suspendu: { label: 'Suspendu', color: 'bg-red-600 text-white' }
};

export default function GestionIdentifiants() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [_selectedUser, _setSelectedUser] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, user: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [showInvitation, setShowInvitation] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [_newPassword, setNewPassword] = useState(null);
  const [purgeEmail, setPurgeEmail] = useState('');
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);

  const getAuthHeader = () => ({
    Authorization: `Bearer ${getToken()}`
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (roleFilter && roleFilter !== 'all') params.append('role', roleFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/invitations/users/list?${params}`,
        { headers: getAuthHeader() }
      );
      setUsers(response.data.users || []);
    } catch {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  }, [roleFilter, searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Reset password
  const handleResetPassword = async (user, sendEmail = true) => {
    try {
      setActionLoading(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/invitations/users/${user.id}/reset-password?send_email=${sendEmail}`,
        {},
        { headers: getAuthHeader() }
      );
      
      setNewPassword(response.data.nouveau_mot_de_passe);
      toast.success(response.data.message);
      
      if (!sendEmail) {
        setActionDialog({ open: true, type: 'show-password', user, data: response.data });
      } else {
        setActionDialog({ open: false, type: null, user: null });
      }
      
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setActionLoading(false);
    }
  };

  // Send credentials
  const handleSendCredentials = async (user) => {
    try {
      setActionLoading(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/invitations/users/${user.id}/send-credentials`,
        {},
        { headers: getAuthHeader() }
      );
      
      if (response.data.email_envoye) {
        toast.success(`Identifiants envoyés à ${user.email}`);
        setActionDialog({ open: true, type: 'show-password', user, data: response.data });
      } else {
        toast.error("Erreur lors de l'envoi de l'email");
      }
      
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setActionLoading(false);
    }
  };

  // Delete user
  const handleDeleteUser = async (user) => {
    try {
      setActionLoading(true);
      await axios.delete(
        `${BACKEND_URL}/api/invitations/users/${user.id}`,
        { headers: getAuthHeader() }
      );
      
      toast.success(`Utilisateur ${user.email} supprimé`);
      setActionDialog({ open: false, type: null, user: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setActionLoading(false);
    }
  };

  // Purge email completely
  const handlePurgeEmail = async (email) => {
    try {
      setActionLoading(true);
      const response = await axios.delete(
        `${BACKEND_URL}/api/invitations/purge-email/${encodeURIComponent(email)}`,
        { headers: getAuthHeader() }
      );
      
      toast.success(response.data.message);
      setShowPurgeDialog(false);
      setPurgeEmail('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la purge');
    } finally {
      setActionLoading(false);
    }
  };

  // Update status
  const handleUpdateStatus = async (user, status) => {
    try {
      setActionLoading(true);
      await axios.put(
        `${BACKEND_URL}/api/invitations/users/${user.id}/status?status=${status}`,
        {},
        { headers: getAuthHeader() }
      );
      
      toast.success(`Statut mis à jour: ${status}`);
      setActionDialog({ open: false, type: null, user: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copié !');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getRoleConfig = (role) => ROLES_CONFIG[role] || ROLES_CONFIG.membre;
  const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.actif;

  return (
    <div className="space-y-6">
      <InstitutionalHero
        icon={Key}
        badge="Sécurité d'accès & RGPD"
        title="Identifiants & Comptes utilisateurs"
        subtitle="Création, renouvellement et envoi sécurisé d'identifiants de connexion pour l'ensemble des rôles de la plateforme (Super Admin · Admin · Cooper · Expert · Membre)."
        gradient="bg-gradient-to-br from-slate-900 via-blue-900 to-cyan-900"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => setShowPurgeDialog(true)}
              className="bg-white/10 hover:bg-white/20 text-white border-white/30 backdrop-blur-sm"
              title="Purger un email bloqué"
            >
              <Eraser className="h-4 w-4 mr-2" />
              Purger email
            </Button>
            <Button
              onClick={() => setShowInvitation(true)}
              className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-bold shadow-md"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Créer un utilisateur
            </Button>
          </>
        }
      />

      <Card>
        <CardContent className="pt-6">
          {/* Filtres */}
          <div className="flex gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, prénom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[200px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filtrer par rôle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les rôles</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="cooper">COOPER&apos;S</SelectItem>
                <SelectItem value="expert">EXPERT</SelectItem>
                <SelectItem value="member">Membre</SelectItem>
                <SelectItem value="beneficiaire_pro">Bénéficiaire Pro</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchUsers}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {/* Stats rapides */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{users.length}</div>
              <div className="text-sm text-gray-600">Total utilisateurs</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">
                {users.filter(u => u.status === 'actif' || !u.status).length}
              </div>
              <div className="text-sm text-gray-600">Actifs</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-600">
                {users.filter(u => u.role === 'cooper').length}
              </div>
              <div className="text-sm text-gray-600">COOPER&apos;S</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-orange-600">
                {users.filter(u => u.role === 'expert').length}
              </div>
              <div className="text-sm text-gray-600">Experts</div>
            </div>
          </div>

          {/* Liste des utilisateurs */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>Aucun utilisateur trouvé</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-gray-600">Utilisateur</th>
                    <th className="text-left p-4 font-medium text-gray-600">Email</th>
                    <th className="text-left p-4 font-medium text-gray-600">Rôle</th>
                    <th className="text-left p-4 font-medium text-gray-600">Statut</th>
                    <th className="text-left p-4 font-medium text-gray-600">Créé le</th>
                    <th className="text-right p-4 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const roleConfig = getRoleConfig(user.role);
                    const statusConfig = getStatusConfig(user.status);
                    const RoleIcon = roleConfig.icon;
                    
                    return (
                      <tr key={user.id} className="border-b hover:bg-gray-50 transition-colors">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${roleConfig.color.split(' ')[0]}`}>
                              <RoleIcon className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="font-medium">{user.prenom} {user.nom}</p>
                              {user.entreprise && (
                                <p className="text-xs text-gray-500">{user.entreprise}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="font-mono text-sm">{user.email}</span>
                        </td>
                        <td className="p-4">
                          <Badge className={roleConfig.color}>{roleConfig.label}</Badge>
                        </td>
                        <td className="p-4">
                          <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                        </td>
                        <td className="p-4 text-sm text-gray-500">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : '-'}
                        </td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setActionDialog({ open: true, type: 'send', user })}
                              title="Envoyer les identifiants"
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setActionDialog({ open: true, type: 'reset', user })}
                              title="Renouveler le mot de passe"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setActionDialog({ open: true, type: 'status', user })}
                              title="Changer le statut"
                            >
                              {user.status === 'actif' || !user.status ? (
                                <UserX className="h-4 w-4 text-orange-500" />
                              ) : (
                                <UserCheck className="h-4 w-4 text-green-500" />
                              )}
                            </Button>
                            {user.role !== 'super_admin' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setActionDialog({ open: true, type: 'delete', user })}
                                title="Supprimer"
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog d'actions */}
      <IdentifiantsActionDialog
        actionDialog={actionDialog}
        setActionDialog={setActionDialog}
        actionLoading={actionLoading}
        copiedField={copiedField}
        copyToClipboard={copyToClipboard}
        getRoleConfig={getRoleConfig}
        handleDeleteUser={handleDeleteUser}
        handleResetPassword={handleResetPassword}
        handleSendCredentials={handleSendCredentials}
        handleUpdateStatus={handleUpdateStatus}
      />

      {/* Dialog Invitation */}
      <InvitationDialog
        isOpen={showInvitation}
        onClose={() => setShowInvitation(false)}
        onSuccess={() => {
          fetchUsers();
          setShowInvitation(false);
        }}
      />

      {/* Dialog Purge Email */}
      <Dialog open={showPurgeDialog} onOpenChange={setShowPurgeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Eraser className="h-5 w-5" />
              Purger un email bloqué
            </DialogTitle>
            <DialogDescription>
              Cette fonction supprime TOUTES les traces d&apos;un email dans la base de données.
              Utile si vous obtenez l&apos;erreur &quot;email déjà utilisé&quot; après avoir supprimé un utilisateur.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <AlertTriangle className="h-5 w-5 text-orange-600 mb-2" />
              <p className="text-sm text-orange-800">
                <strong>Attention :</strong> Cette action supprimera définitivement toutes les données
                associées à cet email (utilisateur, invitations, membre, etc.)
              </p>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Email à purger</label>
              <Input
                type="email"
                placeholder="exemple@email.com"
                value={purgeEmail}
                onChange={(e) => setPurgeEmail(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowPurgeDialog(false); setPurgeEmail(''); }}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handlePurgeEmail(purgeEmail)} 
              disabled={actionLoading || !purgeEmail.includes('@')}
            >
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eraser className="h-4 w-4 mr-2" />}
              Purger cet email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
